/* The route editor's state: points, the undo stack, and what is dirty.
 *
 * Pure and headless. The map wires gestures to these operations and draws
 * `points`; nothing here knows about MapLibre, which is what makes the
 * awkward parts testable.
 *
 * ═══ WHAT FREEBOARD-SK LEARNED THE HARD WAY ═══
 *
 * Read before changing the gesture rules. freeboard-sk's `vertex-delete.ts` and
 * `route-extend.ts` cite the issue numbers and are a specification of the bugs
 * a hand-rolled editor otherwise rediscovers on the water:
 *
 * #608 — A LONG-PRESS DELETE ALSO FIRES A CLICK. The release that completes the
 *   delete produces a click, and by the time it is handled the delete has moved
 *   the line away from the pixel that was clicked. A hit-test therefore says
 *   "open water", and an open-water click extends the route: deleting a vertex
 *   immediately adds one back. A hit-test alone CANNOT distinguish the two.
 *   The gesture that deleted must refuse to extend — `deletedInGesture` below.
 *
 * #549 — a click in open water appends an end point, so a route can be extended
 *   without leaving edit mode.
 *
 * #581 — after an edit, the leg to report is the one ENDING at the vertex
 *   touched, because legs are numbered by their start vertex.
 *
 * Their marker is scoped to a gesture ID rather than a bare boolean, because
 * OpenLayers delays `singleclick` by 250 ms and a fast follow-up press can open
 * a new gesture while the old click is still pending. Ours carries the same id
 * for the same reason: MapLibre's `click` likewise arrives after `mouseup`.
 */

/* BoundPoint lives beside LatLon in chart-core now. The `wptId`/`wptName`
 * fields are a SignalK idea and stay OPTIONAL — a hosted chart's route points
 * simply never have them, and the editor has never cared either way. */
import type { BoundPoint, LatLon } from './geo.ts';

export type EditMode = 'off' | 'draft' | 'stored';

/**
 * A route point, which may BE a waypoint that already exists on the server.
 *
 * The shape lives in `$signalk/resources` beside the code that writes it. What
 * matters HERE is why the binding rides on the point rather than in a parallel
 * array: every operation below replaces points wholesale, so a point dragged off
 * its waypoint LOSES its binding for free, and one that is deleted takes its
 * binding with it. No index bookkeeping, and therefore no index bugs.
 *
 * That is not a tidiness argument. The previous code kept `coordinatesMeta`
 * whenever its LENGTH still matched the geometry, so moving one point of the
 * boat's real eight-point route left all eight links intact and the moved one
 * went on claiming to be a waypoint half a mile away.
 */
export type EditPoint = BoundPoint;

/** One reversible operation. The whole point list, because they are short and
 *  a diff-based stack is a source of bugs for no measurable gain. */
interface Snapshot {
  pts: EditPoint[];
  label: string;
}

const MAX_UNDO = 100;

class RouteEditor {
  mode = $state<EditMode>('off');
  /** Resource id when editing something stored; null for a fresh draft. */
  id = $state<string | null>(null);
  name = $state('');
  pts = $state<EditPoint[]>([]);
  /** Index of the selected vertex, or null. */
  selected = $state<number | null>(null);
  /** True once anything has changed since the last save. */
  dirty = $state(false);

  /* What the point under the finger would bind to right now.
   *
   * NEVER SILENTLY — the plan's word, and the right one. Binding moves the point
   * and changes what the route means on the Axiom, so it has to be visible while
   * it is happening rather than discovered afterwards in a saved route. Set by
   * the page while a gesture is live and cleared on release: it describes a
   * gesture, not the route, so nothing about it is persisted or undoable. */
  snap = $state<{ name: string; m: number } | null>(null);

  /** How many points are waypoints that already exist. For the summary. */
  get boundCount(): number {
    void this.rev;
    return this.pts.reduce((n, p) => n + (p.wptId ? 1 : 0), 0);
  }

  #undo: Snapshot[] = [];
  #redo: Snapshot[] = [];
  /** Bumped so the map layer rebuilds. */
  rev = $state(0);

  get active(): boolean {
    return this.mode !== 'off';
  }
  get canUndo(): boolean {
    void this.rev;
    return this.#undo.length > 0;
  }
  get canRedo(): boolean {
    void this.rev;
    return this.#redo.length > 0;
  }
  get undoLabel(): string {
    void this.rev;
    return this.#undo[this.#undo.length - 1]?.label ?? '';
  }

  /* ---- gesture identity, per #608 ---- */

  #gesture = 0;
  #deletedIn: number | null = null;

  /** Open a new pointer gesture. Called on pointerdown. */
  startGesture(): number {
    this.#gesture++;
    return this.#gesture;
  }

  /** This gesture deleted a vertex, so its click must not extend the route. */
  markDeleted(): void {
    this.#deletedIn = this.#gesture;
  }

  /** Whether the click being handled is the tail of a delete. */
  get deletedInGesture(): boolean {
    return this.#deletedIn !== null;
  }

  /* Retire the marker once the click it belongs to has resolved, or when the
   * gesture that set it starts dragging — a dragging gesture emits no click, so
   * nothing would consume the marker and it would go stale and suppress a later,
   * legitimate extend. Only that gesture's own drag counts. */
  clearDeleted(): void {
    this.#deletedIn = null;
  }
  clearDeletedIfMine(): void {
    if (this.#deletedIn === this.#gesture) this.#deletedIn = null;
  }

  /* ---- session ---- */

  /**
   * Start a fresh route. Draft-first: usable, activatable, unsaved.
   *
   * `first` may be the route's whole point list at once — the autorouter
   * (M17.6) hands back an already-simplified set of points in one call, and
   * loading them one at a time through `append()` would push one "add
   * point" undo entry per point, so undoing an autorouted result would peel
   * it off a point at a time rather than discarding the suggestion whole.
   * Loading it here, the way a fresh draft always starts, means the FIRST
   * real edit is what gets an undo entry, not the load itself.
   */
  startDraft(first?: EditPoint | EditPoint[]): void {
    const pts = Array.isArray(first) ? first : first ? [first] : [];
    this.mode = 'draft';
    this.id = null;
    this.name = '';
    this.pts = pts;
    this.selected = pts.length === 1 ? 0 : null;
    this.dirty = pts.length > 0;
    this.#undo = [];
    this.#redo = [];
    this.#renaming = null;
    this.rev++;
  }

  /** Edit a route already on the server. */
  edit(id: string, name: string, pts: EditPoint[]): void {
    this.mode = 'stored';
    this.id = id;
    this.name = name;
    this.pts = pts.map((p) => ({ ...p }));
    this.selected = null;
    this.dirty = false;
    this.#undo = [];
    this.#redo = [];
    this.#renaming = null;
    this.rev++;
  }

  cancel(): void {
    this.mode = 'off';
    this.id = null;
    this.pts = [];
    this.selected = null;
    this.dirty = false;
    this.snap = null;
    this.#undo = [];
    this.#redo = [];
    this.#renaming = null;
    this.clearDeleted();
    this.rev++;
  }

  /**
   * After a successful save. Keeps editing, but nothing is outstanding.
   *
   * `pts`, when given, replaces the point list with what was actually
   * written — M17.2's SAVE creates a waypoint for every unbound point before
   * writing the route, and unless that result lands back here, a second SAVE
   * with no further edits would find the same points unbound and create a
   * second set. Not pushed through undo: this is bookkeeping after a write
   * that already happened, not a user edit to step back through.
   */
  saved(id: string, pts?: EditPoint[]): void {
    this.id = id;
    this.mode = 'stored';
    this.dirty = false;
    if (pts) this.pts = pts;
    this.rev++;
  }

  /* ---- operations ---- */

  #push(label: string): void {
    this.#undo.push({ pts: this.pts.map((p) => ({ ...p })), label });
    if (this.#undo.length > MAX_UNDO) this.#undo.shift();
    // Any new edit invalidates the redo branch; keeping it would let a user
    // redo their way into a route that never existed.
    this.#redo = [];
  }

  /** Append to the end — a click in open water. #549. */
  append(p: EditPoint): void {
    this.#push('add point');
    this.pts = [...this.pts, p];
    this.selected = this.pts.length - 1;
    this.dirty = true;
    this.rev++;
  }

  /** Insert into a leg — dragging the line between two vertices. */
  insert(legIndex: number, p: EditPoint): void {
    if (legIndex < 0 || legIndex >= this.pts.length - 1) return;
    this.#push('insert point');
    const next = [...this.pts];
    next.splice(legIndex + 1, 0, p);
    this.pts = next;
    this.selected = legIndex + 1;
    // Every index at or past the insertion shifted by one; whatever #renaming
    // pointed at, it is not that point any more.
    this.#renaming = null;
    this.dirty = true;
    this.rev++;
  }

  /* Moving is committed ONCE per drag, not per frame — otherwise a single drag
   * fills the undo stack with a hundred intermediate positions and undo becomes
   * useless. beginMove snapshots, moveTo updates without pushing. */
  beginMove(i: number): void {
    if (i < 0 || i >= this.pts.length) return;
    this.#push('move point');
    this.selected = i;
  }

  moveTo(i: number, p: EditPoint): void {
    if (i < 0 || i >= this.pts.length) return;
    const next = [...this.pts];
    next[i] = p;
    this.pts = next;
    this.dirty = true;
    this.rev++;
  }

  /** Which point is mid-rename, so the FIRST keystroke pushes one undo entry
   *  and the rest do not — M5.3, "a whole rename is one undo" like a drag. */
  #renaming: number | null = null;

  /**
   * Type a name into a point (M5.3). No `beginRename` companion: unlike a
   * drag there is no pointerup to mark where a rename ends, so `setName`
   * itself notices the first call for a given index and pushes there.
   * `pointName` is what `ensureRouteWaypoints` reads at save time — it
   * creates the waypoint's name if the point has none yet, or renames the
   * one it is already bound to.
   */
  setName(i: number, name: string): void {
    if (i < 0 || i >= this.pts.length) return;
    if (this.#renaming !== i) this.#push('rename point');
    this.#renaming = i;
    const next = [...this.pts];
    next[i] = { ...next[i]!, pointName: name };
    this.pts = next;
    this.selected = i;
    this.dirty = true;
    this.rev++;
  }

  /** Retires the "still typing" marker — call on blur. Leaving it set would
   *  let a LATER rename of the same index (a fresh focus, a new keystroke)
   *  be mistaken for a continuation of this one and skip its own undo push. */
  endRename(): void {
    this.#renaming = null;
  }

  remove(i: number): void {
    if (i < 0 || i >= this.pts.length) return;
    this.#push('delete point');
    this.pts = this.pts.filter((_, n) => n !== i);
    this.selected = null;
    // Whatever index was mid-rename no longer names the same point, deleted
    // or merely shifted — either way the "same gesture" test above stops
    // meaning anything.
    this.#renaming = null;
    this.dirty = true;
    this.rev++;
  }

  /** M17.8: the autorouter recomputed the draft after its destination was
   *  dragged. Swaps the points WITHOUT a fresh `startDraft`, which would clear
   *  the undo stack and throw away the `beginMove` snapshot the drag already
   *  pushed — a whole drag, including the recompute it triggered, stays ONE
   *  undo back to the pre-drag route, exactly as it was before recalc existed.
   *
   *  ═══ IT KEEPS THE ROUTE'S IDENTITY (fixed 14 Aug 2026) ═══
   *
   *  This used to set `mode = 'draft'` and `id = null`, on the assumption that
   *  a recalc only ever happened to an unsaved draft. It does not: `commitRoute`
   *  never cleared `autoroutedDraft`, so a SAVED autoroute whose destination was
   *  dragged came straight back through here and was quietly demoted to a
   *  nameless draft. The next SAVE then wrote a SECOND route resource, and
   *  because the incoming points carry no bindings, `ensureRouteWaypoints`
   *  minted a duplicate waypoint for every point — on the boat's actual
   *  chartplotter, which is the failure `snap.ts` and `mergedProperties` exist
   *  to prevent. Worse, with `id` null the layer's `r.id !== editor.id` filter
   *  stopped hiding the stale server copy, so it redrew underneath the draft.
   *
   *  A recalc replaces GEOMETRY. What is being edited does not change. */
  recalc(pts: EditPoint[]): void {
    this.pts = pts;
    this.selected = null;
    this.dirty = true;
    this.#renaming = null;
    this.rev++;
  }

  undo(): void {
    const s = this.#undo.pop();
    if (!s) return;
    this.#redo.push({ pts: this.pts.map((p) => ({ ...p })), label: s.label });
    this.pts = s.pts;
    this.selected = null;
    this.#renaming = null;
    this.dirty = true;
    this.rev++;
  }

  redo(): void {
    const s = this.#redo.pop();
    if (!s) return;
    this.#undo.push({ pts: this.pts.map((p) => ({ ...p })), label: s.label });
    this.pts = s.pts;
    this.selected = null;
    this.#renaming = null;
    this.dirty = true;
    this.rev++;
  }
}

export const editor = new RouteEditor();

/* ---- geometry the gestures need ---- */

/** Metres between two positions. Equirectangular, as everywhere else here. */
export function metres(a: LatLon, b: LatLon): number {
  const dN = (b.lat - a.lat) * 111_320;
  const dE = (b.lon - a.lon) * 111_320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(dN, dE);
}

/**
 * Which leg a point falls on, and how far it is from that leg, working in
 * SCREEN pixels rather than metres.
 *
 * Screen space is what the finger is in: a 12-pixel tolerance is a constant
 * hit target at every zoom, where 12 metres is untouchable at harbour scale and
 * swallows the whole route at passage scale.
 */
export function nearestLeg(
  ptsPx: { x: number; y: number }[],
  px: { x: number; y: number }
): { index: number; dist: number; at: { x: number; y: number } } | null {
  if (ptsPx.length < 2) return null;
  let best: { index: number; dist: number; at: { x: number; y: number } } | null = null;
  for (let i = 0; i < ptsPx.length - 1; i++) {
    const a = ptsPx[i]!;
    const b = ptsPx[i + 1]!;
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const len2 = vx * vx + vy * vy;
    // A zero-length leg has no interior to insert into; skip rather than
    // dividing by zero and inserting at a NaN.
    if (len2 === 0) continue;
    let t = ((px.x - a.x) * vx + (px.y - a.y) * vy) / len2;
    t = Math.max(0, Math.min(1, t));
    const at = { x: a.x + t * vx, y: a.y + t * vy };
    const d = Math.hypot(px.x - at.x, px.y - at.y);
    if (!best || d < best.dist) best = { index: i, dist: d, at };
  }
  return best;
}

/** Nearest vertex within `tol` pixels, or null. */
export function nearestVertex(
  ptsPx: { x: number; y: number }[],
  px: { x: number; y: number },
  tol: number
): number | null {
  let bi: number | null = null;
  let bd = tol;
  for (let i = 0; i < ptsPx.length; i++) {
    const d = Math.hypot(ptsPx[i]!.x - px.x, ptsPx[i]!.y - px.y);
    if (d <= bd) {
      bd = d;
      bi = i;
    }
  }
  return bi;
}

/** How close a recomputed point must be to an old one to inherit its waypoint
 *  binding, in metres. The cost grid's cell is ~153 m and the router returns
 *  cell CENTRES, so a point the router "did not move" can still land up to
 *  about half a cell away. 120 m keeps that case and is far tighter than the
 *  spacing between distinct waypoints on a route. */
export const REBIND_TOLERANCE_M = 120;

/**
 * Carry waypoint bindings across an autoroute recompute.
 *
 * ═══ WHY ═══
 *
 * The router answers in bare lat/lon: it knows nothing about waypoints. Feeding
 * that straight into the editor discards every `wptId`, and `ensureRouteWaypoints`
 * then creates a brand-new waypoint for each point on the next save — so
 * dragging a saved autoroute's destination littered the Axiom with a duplicate
 * of every waypoint the route already had.
 *
 * Most points genuinely move and SHOULD be new. The ones that do not — above
 * all the start, which `recalcAutoroutedDraft` holds fixed — keep their binding
 * and therefore their waypoint, its name, and the Raymarine GUID riding on it.
 *
 * Each old binding is claimed at most once, nearest first, so two recomputed
 * points near one old waypoint cannot both inherit it and produce a route that
 * names the same waypoint twice.
 */
export function carryBindings(
  previous: readonly EditPoint[],
  next: readonly { lat: number; lon: number }[],
  tolM = REBIND_TOLERANCE_M
): EditPoint[] {
  /* Every candidate pairing inside the tolerance, then assigned NEAREST FIRST.
   *
   * Walking `next` in order and letting each point take the closest binding
   * still available is not the same thing, and gets it wrong: a point 89 m from
   * a waypoint would claim it before a later point 11 m away had a chance. The
   * start of a recomputed route is the point most likely to be genuinely
   * unchanged, so letting an earlier, sloppier match steal its waypoint is
   * exactly the case worth getting right. */
  const pairs: { ni: number; pi: number; d: number }[] = [];
  for (let ni = 0; ni < next.length; ni++) {
    for (let pi = 0; pi < previous.length; pi++) {
      const q = previous[pi]!;
      if (!q.wptId) continue;
      const d = metres(next[ni]!, q);
      if (d <= tolM) pairs.push({ ni, pi, d });
    }
  }
  pairs.sort((a, b) => a.d - b.d);

  const takenNext = new Set<number>();
  const takenPrev = new Set<number>();
  const bind = new Map<number, EditPoint>();
  for (const { ni, pi } of pairs) {
    if (takenNext.has(ni) || takenPrev.has(pi)) continue;
    takenNext.add(ni);
    takenPrev.add(pi);
    bind.set(ni, previous[pi]!);
  }

  return next.map((p, ni) => {
    const q = bind.get(ni);
    return q
      ? { lat: p.lat, lon: p.lon, wptId: q.wptId, wptName: q.wptName }
      : { lat: p.lat, lon: p.lon };
  });
}
